import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle, faCircleDot } from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import symbols from '../../../questions/astrology/symbols.json';
import Question from '../../../components/Question';

const SignsLesson = ({ onNextLesson, markLessonComplete, currentQuestion = 0, onQuestionChange }) => {
  const chapter = symbols.find(c => c.chapter === "Chapter 2: Astrological Symbols and Terms");
  const lessonData = chapter ? chapter.lessons.find(l => l.title === "Lesson 1: Sign Symbols") : null;

  const [completedQuestions, setCompletedQuestions] = useState(lessonData ? Array(lessonData.sections.length).fill(false) : []);
  const [feedback, setFeedback] = useState(null);

  if (!lessonData || !lessonData.sections) {
    return <div>Error: Lesson data not found. Please check the JSON structure.</div>;
  }

  const handleQuestionChange = (newQuestionIndex) => {
    onQuestionChange(newQuestionIndex);
    setFeedback(null);
  };

  const handleAnswer = (isCorrect) => {
    if (isCorrect) {
      setFeedback('Correct!');
      const newCompletedQuestions = [...completedQuestions];
      newCompletedQuestions[currentQuestion] = true;
      setCompletedQuestions(newCompletedQuestions);
      
      // Wait a moment before moving to next question
      setTimeout(() => {
        if (currentQuestion === lessonData.sections.length - 1) {
          markLessonComplete();
          onNextLesson();
        } else {
          handleQuestionChange(currentQuestion + 1);
        }
      }, 1500);
    } else {
      setFeedback(`Incorrect. The correct answer is: ${currentSectionData.correctAnswer}`);
      // Clear feedback after 3 seconds
      setTimeout(() => {
        setFeedback(null);
      }, 3000);
    }
  };

  const handleSymbolClick = (option) => {
    if (feedback) return; // Prevent clicking if feedback is showing
    const isCorrect = option === currentSectionData.correctAnswer;
    handleAnswer(isCorrect);
  };

  const handleDotClick = (index) => {
    if (index <= currentQuestion || completedQuestions[index]) {
      handleQuestionChange(index);
    }
  };

  const transition = { duration: 0.15, ease: "easeInOut" };

  const currentSectionData = lessonData.sections[currentQuestion];

  return (
    <div>
      <Helmet>
        <title>{`${currentSectionData.title}`}</title>
      </Helmet>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentQuestion}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={transition}
        >
          {currentSectionData.imageUrl ? (
            <div className="image-question">
              <img
                src={currentSectionData.imageUrl}
                alt={currentSectionData.title}
                className="smallimage"
              />
              <Question
                question={currentSectionData.question}
                options={currentSectionData.options}
                correctAnswer={currentSectionData.correctAnswer}
                onAnswered={handleAnswer}
              />
            </div>
          ) : (
            <div className="text-to-image-question">
              <p className="question">{currentSectionData.question}</p>
              <div className="options">
                {currentSectionData.options.map((option, index) => (
                  <div
                    key={index}
                    style={{
                      cursor: feedback ? 'default' : 'pointer',
                      transition: 'transform 0.2s ease-in-out, filter 0.2s ease-in-out',
                      border: '2px solid transparent',
                      borderRadius: '8px',
                      padding: '8px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      width: '80px',
                      height: '80px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: feedback ? 0.7 : 1
                    }}
                    onClick={(e) => {
                      if (feedback) return;
                      e.stopPropagation();
                      handleSymbolClick(option);
                    }}
                    onMouseEnter={(e) => {
                      if (feedback) return;
                      e.target.style.transform = 'scale(1.1)';
                      e.target.style.borderColor = '#ffc107';
                      e.target.style.background = 'rgba(255, 193, 7, 0.1)';
                      e.target.style.filter = 'brightness(1.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'scale(1)';
                      e.target.style.borderColor = 'transparent';
                      e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                      e.target.style.filter = 'brightness(1)';
                    }}
                  >
                    <img
                      src={option}
                      alt={`Option ${index + 1}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        pointerEvents: 'none'
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Question Dots */}
          <section className="section has-text-centered">
            <div className="column">
              <div className="dots mt-4">
                {lessonData.sections.map((_, index) => (
                  <FontAwesomeIcon
                    key={index}
                    icon={completedQuestions[index] ? faCircleDot : faCircle}
                    className={`mr-2 ${index === currentQuestion ? 'has-text-primary' : completedQuestions[index] ? 'has-text-warning' : 'has-text-grey'} dot-icon`}
                    onClick={() => handleDotClick(index)}
                  />
                ))}
              </div>
              <div className="mt-2">
                <p className="has-text-warning has-text-weight-bold">
                  {completedQuestions.filter(Boolean).length} of {lessonData.sections.length} questions in this lesson completed
                </p>
              </div>
            </div>
          </section>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default SignsLesson;
